// src/pages/contact/Contact.js
import React, { useState } from "react";
import Container from "../../../components/Container";
import api from "../../../api/axios";
import { sendClientErrorLog } from "../../../api/clientErrorLogger";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { NATIONALITY_OPTIONS } from "../../../contants/nationalities";

export default function ContactUs() {
  const { t } = useTranslation();

  // "기타" 라벨 (없으면 기본 '기타')
  const rawOther = t("contact.other");
  const OTHER_VALUE =
    rawOther && rawOther !== "contact.other" ? rawOther : "기타";

  /** 코드 사전 */
  const SERVICE_DICT = {
    CRUISE: { code: "CRUISE", labelKey: "contact.services.cruise" },
    KFOOD: { code: "KFOOD", labelKey: "contact.services.kfood" },
    KOREAEMPLOYMENT: {
      code: "KOREAEMPLOYMENT",
      labelKey: "contact.services.koreaemployment",
    },
  };

  const JOB_DICT = {
    FNB: { code: "FNB", labelKey: "contact.jobs.fnb" },
    BAR: { code: "BAR", labelKey: "contact.jobs.bar" },
    GALLEY: { code: "GALLEY", labelKey: "contact.jobs.galley" },
    HK: { code: "HK", labelKey: "contact.jobs.hk" },
    RECEPTION: { code: "RECEPTION", labelKey: "contact.jobs.reception" },
    ENGINEER: { code: "ENGINEER", labelKey: "contact.jobs.engineer" },
    SECURITY: { code: "SECURITY", labelKey: "contact.jobs.security" },
    ROOMSERVICE: { code: "ROOMSERVICE", labelKey: "contact.jobs.roomservice" },
  };

  const serviceOptions = Object.values(SERVICE_DICT).map((v) => ({
    value: v.code,
    label: t(v.labelKey),
  }));
  const jobOptions = Object.values(JOB_DICT).map((v) => ({
    value: v.code,
    label: t(v.labelKey),
  }));

  // 학력
  const [education, setEducation] = useState([
    { level: "", institution: "", major: "", gradDate: "" },
  ]);

  // 경력
  const [experiences, setExperiences] = useState([
    { company: "", position: "", startDate: "", endDate: "" },
  ]);

  // 어학시험
  const [langTests, setLangTests] = useState([
    {
      exam: "",
      lang: "",
      score: "",
      acquired: "",
      langOther: "",
      examOther: "",
    },
  ]);

  // 자격/면허
  const [licenses, setLicenses] = useState([
    { name: "", issuer: "", acquired: "" },
  ]);

  // 폼 상태
  const [form, setForm] = useState({
    service: "",
    job: "",
    name: "",
    gender: "",
    birthDate: "",
    nationality: "",
    nationalityOther: "",
    livingInKorea: "", // ✅ "YES" | "NO"
    visaType: "",
    address: "",
    email: "",
    phone: "",
    experience: "",
    idPhoto: null,
    resumeFile: null,
    elig1: "",
    elig2: "",
    elig3: "",
    elig4: "",
    elig5: "",
    elig6: "",
    agree: false,
    confirmTruth: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  // 최대 파일 용량 (20MB)
  const MAX_FILE_MB = 20;
  const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
  const RESUME_ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];
  const RESUME_ALLOWED_MIME_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  // ------ 파일 업로드 헬퍼 (Presigned URL) ------
  // const toBase64FileName = (name) => btoa(encodeURIComponent(name));
  const toBase64FileName = (name) => btoa(unescape(encodeURIComponent(name)));

  const requestPresigned = async (type, file) => {
    const fileName = toBase64FileName(file.name);
    const { data } = await api.get("/api/contact/presigned-url", {
      params: { fileName, fileSize: file.size, type }, // type: "idPhoto" | "resume"
    });
    return data.data; // { uploadUrl, objectUrl, key }
  };

  const uploadToS3 = async (uploadUrl, file) => {
    const res = await axios.put(uploadUrl, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
    });
  };
  // ---------------------------------------------

  const isAllowedResumeFile = (file) => {
    if (!file) return true;

    const lowerName = String(file.name || "").toLowerCase();
    const hasAllowedExtension = RESUME_ALLOWED_EXTENSIONS.some((ext) =>
      lowerName.endsWith(ext),
    );
    const hasAllowedMimeType = RESUME_ALLOWED_MIME_TYPES.includes(file.type);

    return hasAllowedExtension || hasAllowedMimeType;
  };

  const onChange = (e) => {
    const { name, type, value, checked, files } = e.target;
    if (type === "file") {
      const nextFile = files?.[0] ?? null;

      if (name === "resumeFile" && nextFile && !isAllowedResumeFile(nextFile)) {
        e.target.value = "";
        setMessage(
          t(
            "contact.invalidResumeFileType",
            "Resume files must be PDF, DOC, or DOCX.",
          ),
        );
        setForm((f) => ({ ...f, resumeFile: null }));
        return;
      }

      setMessage("");
      setForm((f) => ({ ...f, [name]: nextFile }));
    } else if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  // 학력
  const onEduChange = (idx, key, value) => {
    setEducation((eds) => {
      const next = [...eds];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addEducation = () =>
    setEducation((eds) => [
      ...eds,
      { level: "", institution: "", major: "", gradDate: "" },
    ]);
  const removeEducation = (idx) =>
    setEducation((eds) =>
      eds.length <= 1 ? eds : eds.filter((_, i) => i !== idx),
    );

  // 경력
  const onExpChange = (idx, key, value) => {
    setExperiences((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addExperience = () =>
    setExperiences((rows) => [
      ...rows,
      { company: "", position: "", startDate: "", endDate: "" },
    ]);
  const removeExperience = (idx) =>
    setExperiences((rows) =>
      rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx),
    );

  // 어학시험
  const onLangChange = (idx, key, value) => {
    setLangTests((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addLang = () =>
    setLangTests((rows) => [
      ...rows,
      {
        exam: "",
        lang: "",
        score: "",
        acquired: "",
        langOther: "",
        examOther: "",
      },
    ]);
  const removeLang = (idx) =>
    setLangTests((rows) => rows.filter((_, i) => i !== idx));

  // 자격/면허
  const onLicChange = (idx, key, value) => {
    setLicenses((rows) => {
      const next = [...rows];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };
  const addLic = () =>
    setLicenses((rows) => [...rows, { name: "", issuer: "", acquired: "" }]);
  const removeLic = (idx) =>
    setLicenses((rows) => rows.filter((_, i) => i !== idx));

  // 한국 국적인지 / 한국 내 거주 중인지에 따라 비자타입 노출 여부
  const isKoreanNationality = form.nationality === "KR";
  const isLivingInKorea = form.livingInKorea === "YES"; // ✅ YES = 한국 내 거주
  const shouldShowVisaType = !(isKoreanNationality && isLivingInKorea);

  // 추가 유효성 체크
  const extraValidate = () => {
    if (!form.service) return t("contact.needService");
    if (!form.job) return t("contact.needJob");
    if (!form.gender) return t("contact.needGender");
    if (!form.birthDate) return t("contact.needBirthDate");
    if (!form.nationality) return t("contact.needNationality");

    // 국적이 기타일 때 상세 필수
    if (form.nationality === "OTHER" && !form.nationalityOther) {
      return t("contact.needNationalityOther");
    }

    if (!form.livingInKorea) return t("contact.needLivingInKorea");

    // visaType은 더 이상 필수 아님
    if (!form.address) return t("contact.needAddress");
    if (!form.email) return t("contact.needEmail");
    if (!form.phone) return t("contact.needPhone");
    if (!form.idPhoto) return t("contact.needIdPhoto");
    if (!form.agree) return t("contact.needAgree");
    if (!form.confirmTruth) return t("contact.needConfirmTruth");

    // 학력
    if (education.length < 1) return t("contact.needEducation");
    for (const e of education) {
      if (!e.level || !e.institution || !e.gradDate)
        return t("contact.needEducation");
      if (e.level === "UNIVERSITY" && !e.major)
        return t("contact.needEducationMajor");
    }

    // 경력
    if (experiences.length < 1) return t("contact.needExperiences");
    for (const exp of experiences) {
      if (!exp.company || !exp.position || !exp.startDate || !exp.endDate) {
        return t("contact.needExperiences");
      }
    }

    // ---------- 어학시험: 선택 ----------
    const isLangRowEmpty = (l) =>
      !l.exam &&
      !l.lang &&
      !l.score &&
      !l.acquired &&
      !l.langOther &&
      !l.examOther;

    for (const l of langTests) {
      // 완전히 빈 행이면 스킵 → 한 건도 안 적었으면 그냥 통과
      if (isLangRowEmpty(l)) continue;

      // 뭐라도 썼다면 필수 필드는 채워야 함
      if (!l.exam || !l.lang || !l.score || !l.acquired)
        return t("contact.needLangTests");

      if (l.lang === OTHER_VALUE && !l.langOther)
        return t("contact.needLangOther");

      if (l.exam === OTHER_VALUE && !l.examOther)
        return t("contact.needExamOther");
    }

    // ---------- 자격/면허: 선택 ----------
    const isLicRowEmpty = (c) => !c.name && !c.issuer && !c.acquired;

    for (const c of licenses) {
      // 완전 빈 행은 허용
      if (isLicRowEmpty(c)) continue;

      // 뭐라도 썼다면 전부 채우기
      if (!c.name || !c.issuer || !c.acquired) return t("contact.needLicenses");
    }

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const msg = extraValidate();
    if (msg) {
      setMessage(msg);
      return;
    }

    // 🔹 클라이언트 선제 용량 체크
    if (form.idPhoto && form.idPhoto.size > MAX_FILE_BYTES) {
      setMessage(
        `증명사진 파일은 최대 ${MAX_FILE_MB}MB까지 업로드할 수 있습니다.`
      );
      return;
    }
    if (form.resumeFile && form.resumeFile.size > MAX_FILE_BYTES) {
      setMessage(
        `이력서 파일은 최대 ${MAX_FILE_MB}MB까지 업로드할 수 있습니다.`
      );
      return;
    }
    if (form.resumeFile && !isAllowedResumeFile(form.resumeFile)) {
      setMessage(
        t(
          "contact.invalidResumeFileType",
          "Resume files must be PDF, DOC, or DOCX.",
        ),
      );
      return;
    }

    setSubmitting(true);
    let failureStage = "prepare";
    try {
      // 1) Presigned URL 발급 + S3 업로드
      failureStage = "presign:idPhoto";
      const idPresign = await requestPresigned("idPhoto", form.idPhoto);
      failureStage = "upload:idPhoto";
      await uploadToS3(idPresign.uploadUrl, form.idPhoto);
      const idPhotoUrl = idPresign.objectUrl;

      let resumeUrl = "";
      if (form.resumeFile) {
        failureStage = "presign:resume";
        const rePresign = await requestPresigned("resume", form.resumeFile);
        failureStage = "upload:resume";
        await uploadToS3(rePresign.uploadUrl, form.resumeFile);
        resumeUrl = rePresign.objectUrl;
      }

      // 경력 요약 문자열
      const experienceSummary = experiences
        .map(
          (exp) =>
            `${exp.company} / ${exp.position} / ${exp.startDate} ~ ${exp.endDate}`
        )
        .join("\n");

      // 국적: 기타일 경우, 직접 입력한 문자열로 전송
      const nationalityValue =
        form.nationality === "OTHER" && form.nationalityOther
          ? form.nationalityOther
          : form.nationality;

      const ynToBool = (v) => v === "YES";

      // ===== ✅ 빈 행 제거 헬퍼 =====
      const isLangRowEmpty = (l) =>
        !l.exam &&
        !l.lang &&
        !l.score &&
        !l.acquired &&
        !l.langOther &&
        !l.examOther;

      const isLicRowEmpty = (c) => !c.name && !c.issuer && !c.acquired;

      const filteredLangTests = langTests.filter((l) => !isLangRowEmpty(l));
      const filteredLicenses = licenses.filter((c) => !isLicRowEmpty(c));
      // ==============================

      // 2) DTO 규격에 맞는 JSON 페이로드 생성
      const payload = {
        service: form.service,
        job: form.job,
        name: form.name,
        gender: form.gender,
        birthDate: form.birthDate,
        nationality: nationalityValue, // 코드 또는 직접 입력 문자열

        // ✅ livingInKorea: Boolean 으로 전송
        livingInKorea: ynToBool(form.livingInKorea),
        visaType: form.visaType || "",
        address: form.address,
        email: form.email,
        phone: form.phone,

        experience: experienceSummary,

        education: education.map((e) => {
          let degreeText = e.level || "";
          if (e.level === "UNIVERSITY" && e.major) {
            degreeText = `${degreeText} - ${e.major}`;
          }
          return {
            degree: degreeText,
            institution: e.institution,
            gradDate: e.gradDate, // "YYYY-MM"
          };
        }),

        langTests: filteredLangTests.map((l) => ({
          exam: l.exam === OTHER_VALUE && l.examOther ? l.examOther : l.exam,
          lang: l.lang === OTHER_VALUE && l.langOther ? l.langOther : l.lang,
          score: l.score,
          acquired: l.acquired,
        })),

        licenses: filteredLicenses.map((c) => ({
          name: c.name,
          issuer: c.issuer,
          acquired: c.acquired,
        })),

        idPhotoUrl,
        resumeUrl: resumeUrl || "",

        // ✅ elig1~6 도 Boolean 으로 전송
        elig1: ynToBool(form.elig1),
        elig2: ynToBool(form.elig2),
        elig3: ynToBool(form.elig3),
        elig4: ynToBool(form.elig4),
        elig5: ynToBool(form.elig5),
        elig6: ynToBool(form.elig6),

        agree: !!form.agree,
        confirmTruth: !!form.confirmTruth,
      };

      // 3) JSON 전송
      failureStage = "submit";
      await api.post("/api/contact/inquiry", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setMessage(t("contact.success"));

      // 폼 리셋
      setForm((f) => ({
        ...f,
        service: "",
        job: "",
        name: "",
        gender: "",
        birthDate: "",
        nationality: "",
        nationalityOther: "",
        livingInKorea: "",
        visaType: "",
        address: "",
        email: "",
        phone: "",
        experience: "",
        idPhoto: null,
        resumeFile: null,
        elig1: "",
        elig2: "",
        elig3: "",
        elig4: "",
        elig5: "",
        elig6: "",
        agree: false,
        confirmTruth: false,
      }));
      setEducation([{ level: "", institution: "", major: "", gradDate: "" }]);
      setExperiences([
        { company: "", position: "", startDate: "", endDate: "" },
      ]);
      setLangTests([
        {
          exam: "",
          lang: "",
          score: "",
          acquired: "",
          langOther: "",
          examOther: "",
        },
      ]);
      setLicenses([{ name: "", issuer: "", acquired: "" }]);
    } catch (err) {
      await sendClientErrorLog({
        source: "contact-inquiry",
        stage: failureStage,
        err,
        referenceType: "contact",
        referenceLabel: form.service || null,
        fileSnapshot: {
          idPhoto: form.idPhoto
            ? {
                present: true,
                size: form.idPhoto.size,
                type: form.idPhoto.type || null,
              }
            : { present: false },
          resumeFile: form.resumeFile
            ? {
                present: true,
                size: form.resumeFile.size,
                type: form.resumeFile.type || null,
              }
            : { present: false },
        },
        extra: {
          service: form.service || null,
          job: form.job || null,
          route: window.location.pathname,
          resultCode: err?.response?.data?.resultCode ?? null,
          errorCode: err?.code ?? null,
        },
      });
      console.error(err);
      const msg2 = err?.response?.data?.message || t("contact.error");
      setMessage(msg2);
    } finally {
      setSubmitting(false);
    }
  };

  // 번역 배열
  const examOptions = t("contact.exams", { returnObjects: true });
  const languageOptions = t("contact.languages", { returnObjects: true });

  const YesNo = ({ name, label }) => (
    <div className="grid gap-2">
      <div className="text-sm font-medium text-slate-800">{label}</div>
      <div className="flex gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name={name}
            value="YES"
            checked={form[name] === "YES"}
            onChange={onChange}
            required
          />
          <span>{t("contact.yes")}</span>
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name={name}
            value="NO"
            checked={form[name] === "NO"}
            onChange={onChange}
            required
          />
          <span>{t("contact.no")}</span>
        </label>
      </div>
    </div>
  );

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100 via-white to-white" />
        <Container className="py-14">
          <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-[11px]">
            {t("contact.badge")}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight md:text-5xl text-blue-900">
            {t("contact.title")}
          </h1>
          <p className="mt-3 max-w-prose text-slate-600">{t("contact.desc")}</p>
        </Container>
      </section>

      <Container className="py-10">
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-6">
          {/* 신청 서비스 + 지원 직무 */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-blue-900">
              {t("contact.applyService.title")}
            </h2>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label={t("contact.applyService.label")}
                required
                className="md:col-span-2"
              >
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="w-full rounded-xl border bg-white px-3 py-2 outline-none focus:border-blue-300"
                  required
                >
                  <option value="">{t("contact.applyService.ph")}</option>
                  {serviceOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label={t("contact.applyService.job")}
                required
                className="md:col-span-2"
              >
                <select
                  name="job"
                  value={form.job}
                  onChange={onChange}
                  className="w-full rounded-xl border bg-white px-3 py-2 outline-none focus:border-blue-300"
                  required
                >
                  <option value="">{t("contact.applyService.jobPH")}</option>
                  {jobOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          {/* 기본 정보 */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-blue-900">
              {t("contact.section.basic")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("contact.name")} required>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder={t("contact.ph.name")}
                  className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                  required
                />
              </Field>

              <Field label={t("contact.gender.label")} required>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={onChange}
                  className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300 bg-white"
                  required
                >
                  <option value="">{t("contact.select")}</option>
                  <option value="F">{t("contact.gender.female")}</option>
                  <option value="M">{t("contact.gender.male")}</option>
                </select>
              </Field>

              <Field label={t("contact.birthDate")} required>
                <input
                  type="date"
                  name="birthDate"
                  value={form.birthDate}
                  onChange={onChange}
                  className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                  required
                />
              </Field>

              {/* 국적 */}
              <Field label={t("contact.nationality")} required>
                <div className="space-y-2">
                  <select
                    name="nationality"
                    value={form.nationality}
                    onChange={onChange}
                    className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300 bg-white"
                    required
                  >
                    <option value="">{t("contact.select")}</option>
                    {NATIONALITY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {form.nationality === "OTHER" && (
                    <input
                      name="nationalityOther"
                      value={form.nationalityOther}
                      onChange={onChange}
                      placeholder={t("contact.nationalityOtherPH")}
                      className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                    />
                  )}
                </div>
              </Field>

              {/* 한국 내 거주 여부 */}
              <Field label={t("contact.isLivingInKorea")} required>
                <div className="flex gap-4 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="livingInKorea"
                      value="YES"
                      checked={form.livingInKorea === "YES"}
                      onChange={onChange}
                      required
                    />
                    <span>{t("contact.yes")}</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="livingInKorea"
                      value="NO"
                      checked={form.livingInKorea === "NO"}
                      onChange={onChange}
                      required
                    />
                    <span>{t("contact.no")}</span>
                  </label>
                </div>
              </Field>

              {/* 비자 타입 */}
              {shouldShowVisaType && (
                <Field label={t("contact.visaType")}>
                  <input
                    name="visaType"
                    value={form.visaType}
                    onChange={onChange}
                    placeholder={t("contact.visaTypePH")}
                    className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                  />
                </Field>
              )}

              <Field label={t("contact.email")} required>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder={t("contact.ph.email")}
                  className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                  required
                />
              </Field>

              <Field label={t("contact.phone")} required>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder={t("contact.ph.phone")}
                  className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                  required
                />
              </Field>

              <Field
                label={t("contact.address")}
                className="md:col-span-2"
                required
              >
                <input
                  name="address"
                  value={form.address}
                  onChange={onChange}
                  placeholder={t("contact.ph.address")}
                  className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                  required
                />
              </Field>
            </div>
          </section>

          {/* 학력 */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-blue-900">
                {t("contact.section.education")}
              </h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={addEducation}
                  className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  {t("contact.education.add")}
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4">
              {education.map((edu, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border p-4 md:grid md:grid-cols-4 md:gap-3"
                >
                  {/* 고등학교 / 대학교 구분 */}
                  <Field label={t("contact.education.level")} required>
                    <select
                      value={edu.level}
                      onChange={(e) =>
                        onEduChange(idx, "level", e.target.value)
                      }
                      className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300 bg-white"
                      required
                    >
                      <option value="">{t("contact.select")}</option>
                      <option value="HIGH_SCHOOL">
                        {t("contact.education.level.highSchool")}
                      </option>
                      <option value="UNIVERSITY">
                        {t("contact.education.level.university")}
                      </option>
                    </select>
                  </Field>

                  {/* 학교명 */}
                  <Field label={t("contact.institution")} required>
                    <input
                      value={edu.institution}
                      onChange={(e) =>
                        onEduChange(idx, "institution", e.target.value)
                      }
                      placeholder={t("contact.education.institutionPH")}
                      className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      required
                    />
                  </Field>

                  {/* 전공(대학교일 경우에만 노출) */}
                  {edu.level === "UNIVERSITY" && (
                    <Field label={t("contact.education.major")} required>
                      <input
                        value={edu.major}
                        onChange={(e) =>
                          onEduChange(idx, "major", e.target.value)
                        }
                        placeholder={t("contact.education.majorPH")}
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                        required
                      />
                    </Field>
                  )}

                  {/* 졸업연월 */}
                  <Field label={t("contact.gradDate")} required>
                    <input
                      type="month"
                      value={edu.gradDate}
                      onChange={(e) =>
                        onEduChange(idx, "gradDate", e.target.value)
                      }
                      className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      required
                    />
                  </Field>

                  {education.length > 1 ? (
                    <div className="mt-3 flex justify-end md:col-span-4">
                      <button
                        type="button"
                        onClick={() => removeEducation(idx)}
                        className="rounded-lg border px-3 py-1.5 text-xs hover:bg-slate-50"
                      >
                        {t("contact.education.remove")}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <p className="mt-2 text-xs text-slate-500">
              {t("contact.education.note")}
            </p>
          </section>

          {/* 경력 + 어학 + 자격 */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-blue-900">
              {t("contact.section.extras")}
            </h2>

            {/* 경력: 회사명 / 직무 / 근무기간(시작~종료) */}
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-blue-900">
                  {t("contact.experiences.title")}
                </div>
                <button
                  type="button"
                  onClick={addExperience}
                  className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  {t("contact.add")}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {experiences.map((exp, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border p-4 md:grid md:grid-cols-4 md:gap-3"
                  >
                    <Field label={t("contact.experiences.company")} required>
                      <input
                        value={exp.company}
                        onChange={(e) =>
                          onExpChange(idx, "company", e.target.value)
                        }
                        placeholder={t("contact.experiences.companyPH")}
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                        required
                      />
                    </Field>

                    <Field label={t("contact.experiences.position")} required>
                      <input
                        value={exp.position}
                        onChange={(e) =>
                          onExpChange(idx, "position", e.target.value)
                        }
                        placeholder={t("contact.experiences.positionPH")}
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                        required
                      />
                    </Field>

                    <Field label={t("contact.experiences.start")} required>
                      <input
                        type="month"
                        value={exp.startDate}
                        onChange={(e) =>
                          onExpChange(idx, "startDate", e.target.value)
                        }
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                        required
                      />
                    </Field>

                    <Field label={t("contact.experiences.end")} required>
                      <input
                        type="month"
                        value={exp.endDate}
                        onChange={(e) =>
                          onExpChange(idx, "endDate", e.target.value)
                        }
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                        required
                      />
                    </Field>

                    {experiences.length > 1 ? (
                      <div className="mt-3 flex justify-end md:col-span-4">
                        <button
                          type="button"
                          onClick={() => removeExperience(idx)}
                          className="rounded-lg border px-3 py-1.5 text-xs hover:bg-slate-50"
                        >
                          {t("contact.education.remove")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <p className="mt-2 text-xs text-slate-500">
                {t("contact.experiences.note")}
              </p>
            </div>

            {/* 어학시험 */}
            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-blue-900">
                  {t("contact.langTests.title")}
                </div>
                <button
                  type="button"
                  onClick={addLang}
                  className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  {t("contact.add")}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {langTests.map((r, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border p-4 md:grid md:grid-cols-4 md:gap-3"
                  >
                    {/* 언어 + 기타 입력 */}
                    <Field label={t("contact.langTests.lang")}>
                      <div className="space-y-2">
                        <select
                          value={r.lang}
                          onChange={(e) =>
                            onLangChange(idx, "lang", e.target.value)
                          }
                          className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300 bg-white"
                        >
                          <option value="">{t("contact.select")}</option>
                          {languageOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                          {/* 만약 번역 배열에 기타가 없다면 직접 추가 가능 */}
                        </select>
                        {r.lang === OTHER_VALUE && (
                          <input
                            value={r.langOther}
                            onChange={(e) =>
                              onLangChange(idx, "langOther", e.target.value)
                            }
                            placeholder={t("contact.langTests.langOtherPH")}
                            className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                          />
                        )}
                      </div>
                    </Field>

                    {/* 시험 종류 + 기타 입력 */}
                    <Field label={t("contact.langTests.exam")}>
                      <div className="space-y-2">
                        <select
                          value={r.exam}
                          onChange={(e) =>
                            onLangChange(idx, "exam", e.target.value)
                          }
                          className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300 bg-white"
                        >
                          <option value="">{t("contact.select")}</option>
                          {examOptions.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                        {r.exam === OTHER_VALUE && (
                          <input
                            value={r.examOther}
                            onChange={(e) =>
                              onLangChange(idx, "examOther", e.target.value)
                            }
                            placeholder={t("contact.langTests.examOtherPH")}
                            className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                          />
                        )}
                      </div>
                    </Field>

                    <Field label={t("contact.langTests.score")}>
                      <input
                        value={r.score}
                        onChange={(e) =>
                          onLangChange(idx, "score", e.target.value)
                        }
                        placeholder={t("contact.langTests.scorePH")}
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      />
                    </Field>
                    <Field label={t("contact.langTests.acq")}>
                      <input
                        type="month"
                        value={r.acquired}
                        onChange={(e) =>
                          onLangChange(idx, "acquired", e.target.value)
                        }
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      />
                    </Field>

                    <div className="mt-3 flex justify-end md:col-span-4">
                      <button
                        type="button"
                        onClick={() => removeLang(idx)}
                        className="rounded-lg border px-3 py-1.5 text-xs hover:bg-slate-50"
                      >
                        {t("contact.education.remove")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 자격/면허 */}
            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-blue-900">
                  {t("contact.licenses.title")}
                </div>
                <button
                  type="button"
                  onClick={addLic}
                  className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
                >
                  {t("contact.add")}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {licenses.map((r, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border p-4 md:grid md:grid-cols-3 md:gap-3"
                  >
                    <Field label={t("contact.licenses.name")}>
                      <input
                        value={r.name}
                        onChange={(e) =>
                          onLicChange(idx, "name", e.target.value)
                        }
                        placeholder={t("contact.licenses.namePH")}
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      />
                    </Field>
                    <Field label={t("contact.licenses.issuer")}>
                      <input
                        value={r.issuer}
                        onChange={(e) =>
                          onLicChange(idx, "issuer", e.target.value)
                        }
                        placeholder={t("contact.licenses.issuerPH")}
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      />
                    </Field>
                    <Field label={t("contact.licenses.acq")}>
                      <input
                        type="month"
                        value={r.acquired}
                        onChange={(e) =>
                          onLicChange(idx, "acquired", e.target.value)
                        }
                        className="w-full rounded-xl border px-3 py-2 outline-none focus:border-blue-300"
                      />
                    </Field>

                    <div className="mt-3 flex justify-end md:col-span-3">
                      <button
                        type="button"
                        onClick={() => removeLic(idx)}
                        className="rounded-lg border px-3 py-1.5 text-xs hover:bg-slate-50"
                      >
                        {t("contact.education.remove")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 파일 업로드 (presigned 후 JSON 전송) */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-blue-900">
              {t("contact.section.files")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label={t("contact.idPhoto")}
                hint={t("contact.idPhotoHint")}
                required
              >
                <input
                  type="file"
                  name="idPhoto"
                  accept="image/*"
                  onChange={onChange}
                  required
                  className="w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none file:mr-3 file:rounded-lg file:border file:bg-slate-50 file:px-3 file:py-1.5"
                />
              </Field>
              <Field label={t("contact.resumeFile")}>
                <input
                  type="file"
                  name="resumeFile"
                  accept=".pdf,.doc,.docx"
                  onChange={onChange}
                  className="w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none file:mr-3 file:rounded-lg file:border file:bg-slate-50 file:px-3 file:py-1.5"
                />
              </Field>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {t("contact.fileNote")}
            </p>
          </section>

          {/* Eligibility */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-blue-900">
              {t("contact.section.eligibility")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-6">
              <YesNo name="elig1" label={t("contact.elig.q1")} />
              <YesNo name="elig2" label={t("contact.elig.q2")} />
              <YesNo name="elig3" label={t("contact.elig.q3")} />
              <YesNo name="elig4" label={t("contact.elig.q4")} />
              <YesNo name="elig5" label={t("contact.elig.q5")} />
              <YesNo name="elig6" label={t("contact.elig.q6")} />
            </div>
            <p className="mt-3 text-xs text-slate-500">
              {t("contact.eligNote")}
            </p>
          </section>

          {/* 동의 & 제출 */}
          <section className="rounded-2xl border bg-white p-6 shadow-sm">
            {/* 개인정보 처리 동의 */}
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                name="agree"
                checked={form.agree}
                onChange={onChange}
                required
              />
              <a
                target="_blank"
                href="https://loving-pyroraptor-e4b.notion.site/2df6442c8fe981439e83ecc74e4fd33c"
              >
                <span>{t("contact.agree")}</span>
              </a>
            </label>

            {/* 상기 내용 사실 확인 */}
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                name="confirmTruth"
                checked={form.confirmTruth}
                onChange={onChange}
                required
              />
              <span>{t("contact.confirmTruth")}</span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="mt-4 inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? t("contact.submitting") : t("contact.submit")}
            </button>

            {message && (
              <div className="mt-4 rounded-xl border bg-slate-50 p-3 text-sm text-slate-700">
                {message}
              </div>
            )}
          </section>
        </form>
      </Container>
    </main>
  );
}

function Field({ label, required, hint, children, className = "" }) {
  return (
    <label className={`grid gap-1 text-sm ${className}`}>
      <span className="font-medium text-slate-800">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
